import { useUpdateTenantUser, useUpdateSAUser } from 'api/users/service';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardBody, CardHeader, Row, Col, FormGroup, Label, Input, Button } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { formatPhoneNumber, validatePhoneNumber } from '../../utils/phoneUtils';
import { getActiveColorStyle } from '../../utils/colorUtils';
import Select from '../Select';



const suffixOptions = [
  "",
  "Jr.",
  "Sr.",
  "I",
  "II",
  "III",
  "IV",
  "V",
];
const salutationOptions = [
  "",
  "Mr.",
  "Mrs.",
  "Ms.",
  "Dr.",
  "Prof.",
  "Rev.",
  "Sir",
  "Madam",
];

const standardReportAccessOptions = [
  { value: "cannot", label: "Cannot Execute Reports" },
  { value: "can_assigned", label: "Can Execute Assigned Reports" },
  { value: "can_all", label: "Can Execute All Reports" }
];

const customReportAccessOptions = [
  { value: "cannot", label: "Cannot Execute Reports" },
  { value: "can_assigned", label: "Can Execute Assigned Reports" },
  { value: "can_all", label: "Can Execute All Reports" }
];

const customReportEditLevelOptions = [
  { value: "cannot", label: "Cannot Edit Reports" },
  { value: "can_assigned", label: "Can Edit Assigned Reports" },
  { value: "can_all", label: "Can Edit All Reports" }
];



const initialForm = {
  email: '',
  alt_email: '',
  password: '',
  full_name: '',
  tenant_name: '',
  tenant_status: true,
  salutation: '',
  suffix: '',
  title: '',
  work_phone: '',
  home_phone: '',
  cell_phone: '',
  fax: '',
  confirmed: false,
  can_communications_view: false,
  is_report_admin: false,
  standart_report_access: 'cannot',
  custom_report_access: 'can_all',
  custom_report_edit: 'cannot',
};

const GeneralInfo = React.forwardRef(({
  userDetail,
  onSave,
  isSaving: isSavingProp,
  id: propId,
  activeColor = "secondary",
  isTheme2 = false,
  inputStyle,
  inputClassName,
  selectStyles,
  isSA = false,
  tenantId = null,
}, ref) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const updateTenantUserMutation = useUpdateTenantUser();
  const updateSAUserMutation = useUpdateSAUser();
  const navigate = useNavigate();
  const params = useParams();

  // id'yi prop'tan veya route parametresinden al
  const id = propId || params.id;

  useEffect(() => {
    if (userDetail) {
      setFormData({
        ...formData,
        email: userDetail.email || '',
        full_name: userDetail.full_name || '',
        work_phone: userDetail.work_phone || '',
        home_phone: userDetail.home_phone || '',
        cell_phone: userDetail.cell_phone || '',
        confirmed: userDetail.confirmed,
        can_communications_view: userDetail.can_communications_view === true,
        title: userDetail.title || '',
        salutation: userDetail.salutation || '',
        suffix: userDetail.suffix || '',
        alt_email: userDetail.alt_email || '',
        fax: userDetail.fax || '',
        is_report_admin: userDetail.is_report_admin || false,
        standart_report_access: userDetail.standart_report_access || 'cannot',
        custom_report_access: userDetail.custom_report_access || 'can_all',
        custom_report_edit: userDetail.custom_report_edit || 'cannot',
      });

    }
    // eslint-disable-next-line
  }, [userDetail]);

  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({ ...prev, [name]: selectedOption ? selectedOption.value : '' }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleInputChange = (e) => {

    const { name, value, type, checked } = e.target;

    // Apply phone formatting for phone fields
    let formattedValue = value;
    if (name === 'work_phone' || name === 'home_phone' || name === 'cell_phone') {
      formattedValue = formatPhoneNumber(value);
    }

    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : formattedValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = t('userDetail.generalInfo.validation.emailRequired');
    if (!formData.full_name) newErrors.full_name = t('userDetail.generalInfo.validation.fullNameRequired');
    if (formData.password && formData.password.length < 6) {
      newErrors.password = t('userDetail.generalInfo.validation.passwordMinLength');
    }

    // Phone validation using utility
    const phoneError = validatePhoneNumber(formData.work_phone);
    if (phoneError) {
      newErrors.work_phone = phoneError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const userPayload = {
      email: formData.email,
      confirmed: formData.confirmed,
      is_report_admin: formData.is_report_admin,
      standart_report_access: formData.standart_report_access,
      custom_report_access: formData.custom_report_access,
      custom_report_edit: formData.custom_report_edit,
      ...(isSA ? { can_communications_view: formData.can_communications_view === true } : {}),
      ...(formData.password ? { password: formData.password } : {}),
    };

    const userDetailPayload = {
      full_name: formData.full_name,
      salutation: formData.salutation,
      suffix: formData.suffix,
      title: formData.title,
      work_phone: formData.work_phone,
      home_phone: formData.home_phone,
      cell_phone: formData.cell_phone,
      fax: formData.fax,
      alt_email: formData.alt_email,

    };

    if (isSA && tenantId && userDetail?.id) {
      await updateSAUserMutation.mutateAsync({
        id: userDetail.id,
        tenantId,
        userId: id,
        data: { user: userPayload, user_detail: userDetailPayload },
      });
    } else if (typeof updateTenantUserMutation !== 'undefined' && typeof id !== 'undefined' && typeof navigate !== 'undefined') {
      await updateTenantUserMutation.mutateAsync(
        { id: id || '', data: { user: userPayload, user_detail: userDetailPayload } },
      );
    } else if (onSave) {
      onSave({ user: userPayload, user_detail: userDetailPayload });
    }
  };

  React.useImperativeHandle(ref, () => ({
    handleSave
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave();
  };

  const fieldClass = (invalid) =>
    isTheme2 && inputClassName
      ? inputClassName(invalid)
      : invalid
        ? "is-invalid"
        : undefined;

  const fieldStyle = isTheme2 ? inputStyle : { fontSize: "14px" };

  return (
    <Card>
      <CardHeader>
        <b>{t('userDetail.generalInfo.title')}</b>
      </CardHeader>
      <CardBody>
        <form onSubmit={handleSubmit}>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.email')}</Label>
                <Input
                  className={fieldClass(errors.email)}
                  style={fieldStyle}
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  invalid={!!errors.email}
                  required
                />
                {errors.email && <div className="text-danger small">{errors.email}</div>}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.password')}</Label>
                <Input
                  className={fieldClass(errors.password)}
                  style={fieldStyle}
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={t('userDetail.generalInfo.placeholders.password')}
                  invalid={!!errors.password}
                />
                {errors.password && <div className="text-danger small">{errors.password}</div>}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.fullName')}</Label>
                <Input
                  className={fieldClass(errors.full_name)}
                  style={fieldStyle}
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  invalid={!!errors.full_name}
                  required
                />
                {errors.full_name && <div className="text-danger small">{errors.full_name}</div>}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.workPhone')}</Label>
                <Input
                  className={fieldClass(errors.work_phone)}
                  style={fieldStyle}
                  name="work_phone"
                  value={formData.work_phone}
                  onChange={handleInputChange}
                  invalid={!!errors.work_phone}
                  required
                />
                {errors.work_phone && <div className="text-danger small">{errors.work_phone}</div>}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.homePhone')}</Label>
                <Input
                  className={fieldClass()}
                  style={fieldStyle}
                  name="home_phone"
                  value={formData.home_phone}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.cellPhone')}</Label>
                <Input
                  className={fieldClass()}
                  style={fieldStyle}
                  name="cell_phone"
                  value={formData.cell_phone}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.title')}</Label>
                <Input
                  className={fieldClass()}
                  style={fieldStyle}
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.salutation')}</Label>
                <Select
                  activeColor={activeColor}
                  styles={isTheme2 ? selectStyles : undefined}
                  className="react-select"
                  name="salutation"
                  value={salutationOptions.map(opt => ({ value: opt, label: opt || t('userDetail.generalInfo.placeholders.select') })).find(opt => opt.value === formData.salutation)}
                  options={salutationOptions.map(opt => ({ value: opt, label: opt || t('userDetail.generalInfo.placeholders.select') }))}
                  onChange={(option) => handleSelectChange(option, { name: 'salutation' })}
                />

              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.suffix')}</Label>
                <Select
                  activeColor={activeColor}
                  styles={isTheme2 ? selectStyles : undefined}
                  className="react-select"
                  name="suffix"
                  value={suffixOptions.map(opt => ({ value: opt, label: opt || t('userDetail.generalInfo.placeholders.select') })).find(opt => opt.value === formData.suffix)}
                  options={suffixOptions.map(opt => ({ value: opt, label: opt || t('userDetail.generalInfo.placeholders.select') }))}
                  onChange={(option) => handleSelectChange(option, { name: 'suffix' })}
                />

              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.fax')}</Label>
                <Input
                  className={fieldClass()}
                  style={fieldStyle}
                  name="fax"
                  value={formData.fax}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.labels.altEmail')}</Label>
                <Input
                  className={fieldClass()}
                  style={fieldStyle}
                  name="alt_email"
                  value={formData.alt_email}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup check className="mt-3" style={{ paddingLeft: "0px" }}>
                <Label check className="mb-0 d-flex align-items-center">
                  <Input
                    type="checkbox"
                    name="confirmed"
                    className="form-check-input"
                    checked={!!formData.confirmed}
                    onChange={handleInputChange}
                  />
                  <span className="form-check-sign">
                    <span className="check" />
                  </span>
                  <span style={{ marginLeft: 8 }}>{t("userDetail.generalInfo.labels.activeUser")}</span>
                </Label>
              </FormGroup>
            </Col>
            {isSA && (
              <Col md="4">
                <FormGroup check className="mt-3" style={{ paddingLeft: "0px" }}>
                  <Label check className="mb-0 d-flex align-items-center">
                    <Input
                      type="checkbox"
                      name="can_communications_view"
                      className="form-check-input"
                      checked={!!formData.can_communications_view}
                      onChange={handleInputChange}
                    />
                    <span className="form-check-sign">
                      <span className="check" />
                    </span>
                    <span style={{ marginLeft: 8 }}>{t("userDetail.generalInfo.labels.communicationsAccess")}</span>
                  </Label>
                </FormGroup>
              </Col>
            )}
          </Row>

          <hr className="my-2" />
          <CardHeader className='pl-0'>
            <b>{t('userDetail.generalInfo.reportAdmin.title')}</b>
          </CardHeader>
          <Row>
            <Col md="6">
              <FormGroup>
                <FormGroup check className="mt-2" style={{ paddingLeft: "0px" }}>
                  <Label check className="mb-0 d-flex align-items-center">
                    <Input
                      type="checkbox"
                      name="is_report_admin"
                      className="form-check-input"
                      checked={!!formData.is_report_admin}
                      onChange={handleInputChange}
                    />
                    <span className="form-check-sign">
                      <span className="check" />
                    </span>
                    <span style={{ marginLeft: 8 }}>
                      {t("userDetail.generalInfo.reportAdmin.labels.reportAdministrator")}
                    </span>
                  </Label>
                </FormGroup>

              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.reportAdmin.labels.standardReportAccess')}</Label>
                <Select
                  activeColor={activeColor}
                  styles={isTheme2 ? selectStyles : undefined}
                  className="react-select"
                  name="standart_report_access"
                  value={standardReportAccessOptions.find(opt => opt.value === formData.standart_report_access)}
                  options={standardReportAccessOptions.map(opt => ({ ...opt, label: t(`userDetail.generalInfo.reportAdmin.options.${opt.value}`, opt.label) }))}
                  onChange={(option) => handleSelectChange(option, { name: 'standart_report_access' })}
                  isDisabled={formData.is_report_admin}
                />



              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.reportAdmin.labels.customReportAccess')}</Label>
                <Select
                  activeColor={activeColor}
                  styles={isTheme2 ? selectStyles : undefined}
                  className="react-select"
                  name="custom_report_access"
                  value={customReportAccessOptions.find(opt => opt.value === formData.custom_report_access)}
                  options={customReportAccessOptions.map(opt => ({ ...opt, label: t(`userDetail.generalInfo.reportAdmin.options.${opt.value}`, opt.label) }))}
                  onChange={(option) => handleSelectChange(option, { name: 'custom_report_access' })}
                  isDisabled={formData.is_report_admin}
                />



              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>{t('userDetail.generalInfo.reportAdmin.labels.customReportEditLevel')}</Label>
                <Select
                  activeColor={activeColor}
                  styles={isTheme2 ? selectStyles : undefined}
                  className="react-select"
                  name="custom_report_edit"
                  value={customReportEditLevelOptions.find(opt => opt.value === formData.custom_report_edit)}
                  options={customReportEditLevelOptions.map(opt => ({ ...opt, label: t(`userDetail.generalInfo.reportAdmin.options.${opt.value}`, opt.label) }))}
                  onChange={(option) => handleSelectChange(option, { name: 'custom_report_edit' })}
                  isDisabled={formData.is_report_admin}
                />



              </FormGroup>
            </Col>
          </Row>
          {/* Bottom buttons removed as they are now in UserDetail.js header */}
        </form>
      </CardBody>
    </Card>
  );
});

export default GeneralInfo;
